import {
    IDynamicLinkedServiceTypeMetadata,
    ILinkedServiceTypeMetadata
} from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { format, getConnectorLegacyNameByFlag } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { optionsOffOn } from './linked-service-shared-fields';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { IDataflowTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { ConnectionAuthKind, Constants } from 'shell-models-lib';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { type ExposureControlService } from 'app/services/exposure-control/base-exposure-control.service';
import { GlobalVariables } from 'app/services/globals';
import { sapCdcDeprecationDocLink } from 'app/shared/constants/links.const';

const dataflowSapOdpSource: IDataflowTypeMetadata = {
    type: DatasetFormatType.SapODP,
    settings: adfModelTypeMap.sapOdp.dataflowSettings,
    controls: [
        {
            type: 'dataflow-schema-table-selection',
            bind: 'context',
            schemaNameLabel: stringResources.Context_Label,
            schemaNamePlaceholder: format(
                stringResources.DataFlowSchemaTableSelectionSchemaPlaceholder,
                stringResources.Context_Label
            ),
            schemaRequired: true,
            selectionType: 'schema',
            enableCache: false,
            browseSettings: {
                includedSettingsKeys: ['browseContext'],
                schemaLabelKey: 'schemaName',
                schemaValueKey: 'schemaName',
                schemaDescriptionKey: 'description'
            }
        },
        {
            type: 'dataflow-schema-table-selection',
            bind: 'objectName',
            tableNameLabel: stringResources.ODPName_Label,
            tableNamePlaceholder: format(
                stringResources.DataFlowSchemaTableSelectionTablePlaceholder,
                stringResources.Context_Label
            ),
            tableRequired: true,
            selectionType: 'table',
            enableCache: false,
            browseSettings: {
                includedSettingsKeys: ['context'],
                tableLabelKey: 'tableName',
                tableValueKey: 'tableName',
                tableDescriptionKey: 'description'
            }
        },
        {
            type: 'dataflow-sap-odp-run-mode',
            bind: 'readMode'
        },
        {
            type: 'sap-odp-key-columns',
            bind: 'sapOdpKeyColumns',
            required: true,
            hide: `{ readMode.expression === 'fullLoad' }`
        }
    ]
};

export const sapOdpMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const exposureControlService = await getServiceInstance<ExposureControlService>(
        injector,
        'ExposureControlService'
    );
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.sapOdp.linkedServiceType,
        displayName: getConnectorLegacyNameByFlag(stringResources.SapOdp_DisplayText, !__IS_FABRIC_DPE__),
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-Sap'
        },
        isOnPremises: true,
        isSHIRAllowedInDataFlow: true,
        supportDataFlowSource: true,
        supportDatasetV2: true,
        supportFirstClassParameterExperience: true,
        fetchNestedNavTable: true,
        dataflowSource: [dataflowSapOdpSource],
        isFirstClass: true,
        copySourceTypes: exposureControlService.enableCopySapOdp
            ? [adfModelTypeMap.sapOdp.copySourceType, adfModelTypeMap.sapCdc.copySourceType]
            : [],
        copySinkTypes: [],
        onetimeQueryTemplate: '',
        timedQueryTemplate: '',
        category: ['Database'],
        controls: [
            {
                type: 'radio-switch',
                bind: 'logonType',
                label: stringResources.SapTable_LogonType,
                isLabelVertical: true,
                nested: 1,
                options: [
                    {
                        label: stringResources.SapTable_LogonType_ApplicationServer,
                        value: 'applicationServer'
                    },
                    {
                        label: stringResources.GroupLabel,
                        value: 'group'
                    }
                ],
                defaultValue: 'applicationServer',
                cases: [
                    {
                        caseValue: 'applicationServer',
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'server',
                                    label: stringResources.ModelName_ServerName,
                                    required: true,
                                    validators: [{ type: 'required', errorMessage: '' }],
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'systemNumber',
                                    label: stringResources.ModelName_SystemNumber,
                                    required: true,
                                    validators: [{ type: 'required', errorMessage: '' }],
                                    isLabelVertical: true
                                }
                            }
                        ]
                    },
                    {
                        caseValue: 'group',
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'messageServer',
                                    label: stringResources.ModelName_MessageServer,
                                    required: true,
                                    validators: [{ type: 'required', errorMessage: '' }],
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'messageServerService',
                                    label: stringResources.ModelName_MessageServerService,
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'systemId',
                                    label: stringResources.ModelName_SystemId,
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'logonGroup',
                                    label: stringResources.ModelName_LogonGroup,
                                    isLabelVertical: true
                                }
                            }
                        ]
                    }
                ]
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox',
                    bind: 'clientId',
                    label: stringResources.ModelName_ClientId,
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }],
                    isLabelVertical: true
                }
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox',
                    bind: 'language',
                    label: stringResources.Language,
                    isLabelVertical: true
                }
            },
            {
                type: 'radio-switch',
                nested: 1,
                bind: 'sncMode',
                label: stringResources.ModelName_SncMode,
                isLabelVertical: true,
                options: optionsOffOn,
                defaultValue: false,
                cases: [
                    {
                        caseValue: true,
                        children: [
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'sncMyName',
                                    label: stringResources.ModelName_SncMyName,
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'sncPartnerName',
                                    label: stringResources.ModelName_SncPartnerName,
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'sncLibraryPath',
                                    label: stringResources.ModelName_SncLibraryPath,
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'parameterizable',
                                child: {
                                    type: 'textbox',
                                    bind: 'x509CertificatePath',
                                    label: stringResources.ModelName_X509CertificatePath,
                                    isLabelVertical: true
                                }
                            },
                            {
                                type: 'parameterizable-dropdown',
                                bind: 'sncQop',
                                label: stringResources.ModelName_SncQop,
                                options: [
                                    {
                                        label: stringResources.None,
                                        value: undefined
                                    },
                                    {
                                        label: stringResources.Authentication,
                                        value: '1'
                                    },
                                    {
                                        label: stringResources.SapTable_SncQop_Integrity,
                                        value: '2'
                                    },
                                    {
                                        label: stringResources.SapTable_SncQop_Privacy,
                                        value: '3'
                                    },
                                    {
                                        label: stringResources.Default,
                                        value: '8'
                                    },
                                    {
                                        label: stringResources.MaximumLabel,
                                        value: '9'
                                    }
                                ],
                                isLabelVertical: true
                            },
                            {
                                type: 'parameterizable',
                                dynamicContentTextClass: 'input-spaced',
                                child: {
                                    type: 'checkbox',
                                    bind: 'isSSO',
                                    label: stringResources.ModelName_IsSSO,
                                    defaultValue: true,
                                    description: stringResources.SapOdp_IsSSO_Description
                                }
                            }
                        ]
                    }
                ]
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox',
                    bind: 'userName',
                    label: stringResources.ModelName_UserName,
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }],
                    isLabelVertical: true
                }
            },
            {
                type: 'dynamic-akv-switcher',
                bind: 'password',
                label: stringResources.ModelName_Password,
                enableAKVParameterization: true,
                child: {
                    type: 'parameterizable',
                    attribute: 'password',
                    disableAddParameter: true,
                    child: {
                        type: 'password',
                        label: stringResources.ModelName_Password,
                        required: true,
                        isLabelVertical: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                },
                description: format(
                    stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                    format(stringResources.ModelName_LinkedServiceTemplate, stringResources.SapOdp_DisplayText),
                    stringResources.ModelName_Password
                )
            },
            {
                type: 'parameterizable',
                child: {
                    type: 'textbox',
                    bind: 'subscriberName',
                    label: stringResources.ModelName_SubscriberName,
                    isLabelVertical: true,
                    validators: [
                        {
                            type: 'pattern',
                            pattern: Constants.sapCdcSubscriberNameAndProcessPattern.source,
                            errorMessage: format(
                                stringResources.Validation_SapCDCSubscriberNameAndProcess,
                                stringResources.ModelName_SubscriberName
                            )
                        }
                    ]
                }
            }
        ],
        warningMessage: __IS_FABRIC_DPE__ ? undefined : stringResources.Connector_SapCdc_DeprecationWarning,
        warningMessageHyperlink: __IS_FABRIC_DPE__ ? undefined : sapCdcDeprecationDocLink,
        warningTooltip: __IS_FABRIC_DPE__ ? undefined
            : `${stringResources.Connector_SapCdc_DeprecationHint} <a target="_blank" rel="noopener noreferrer" href="${sapCdcDeprecationDocLink}">${stringResources.LearnMore}</a>`,

        // #region Fabric
        supportedInTrident: GlobalVariables.tridentBatch1ConnectorPhase2,
        supportOPDG: GlobalVariables.enableOPDG,
        supportedAuthKindsInTrident: [ConnectionAuthKind.Basic]
        // #endregion
    };

    return metadata;
};
